import { format, isDate } from 'date-fns'
import enUS from 'date-fns/locale/en-US'

const localeImportMap = {
	es: () => import(/* webpackChunkName: "locale-es" */ 'date-fns/locale/es'),
	pt: () => import(/* webpackChunkName: "locale-pt" */ 'date-fns/locale/pt'),
	de: () => import(/* webpackChunkName: "locale-de" */ 'date-fns/locale/de'),
	it: () => import(/* webpackChunkName: "locale-it" */ 'date-fns/locale/it'),
	fr: () => import(/* webpackChunkName: "locale-fr" */ 'date-fns/locale/fr'),
	sv: () => import(/* webpackChunkName: "locale-sv" */ 'date-fns/locale/sv'),
	tr: () => import(/* webpackChunkName: "locale-tr" */ 'date-fns/locale/tr'),
	pl: () => import(/* webpackChunkName: "locale-pl" */ 'date-fns/locale/pl'),
	nb: () => import(/* webpackChunkName: "locale-nb" */ 'date-fns/locale/nb'),
	nl: () => import(/* webpackChunkName: "locale-nl" */ 'date-fns/locale/nl'),
	da: () => import(/* webpackChunkName: "locale-da" */ 'date-fns/locale/da'),
	ar: () => import(/* webpackChunkName: "locale-ar" */ 'date-fns/locale/ar'),
	ja: () => import(/* webpackChunkName: "locale-ja" */ 'date-fns/locale/ja'),
}

class DateProvider {
	fallbackLocale = 'en'
	customFormatsMap = {
		'MMMM d': {
			es: "d 'de' MMMM", // 25 de octubre
			pt: "d 'de' MMMM", // 25 de outubro
			fr: 'd MMMM', // 25 octobre
			de: 'do MMMM', // 25. Oktober
			it: 'd MMMM', // 25 ottobre
			sv: 'd MMMM', // 25 oktober
			tr: 'd MMMM', // 25 ekim
			pl: 'd MMMM', // 25 października
			da: 'do MMMM', // 25. oktober
			nl: 'd MMMM', // 25 oktober
			nb: 'do MMMM', // 25. oktober,
			ja: 'M月d日', // 11月26日
		},
		'LLL, yyyy': {
			ja: 'yyyy年M月', // 2025年11月
		},
	}

	constructor({ locale }) {
		this.locale = this._overrideLocale(locale)

		this.formatOptions = { locale: enUS }
	}

	async loadLocaleAsync() {
		if (window.language === 'en') {
			return
		}

		this.locale = this._overrideLocale(window.language)

		if (!localeImportMap[this.locale]) {
			return
		}

		try {
			const localeModule = await localeImportMap[this.locale]()
			this.formatOptions = { locale: localeModule.default }
		} catch (error) {
			/* eslint-disable no-console */
			console.error(`Failed to load locale ${this.locale}:`, error)
			/* eslint-enable no-console */
		}
	}

	/**
	 * @param {Date} date
	 * @param {string} formatStr - See https://date-fns.org/v2.30.0/docs/format for format options.
	 * @returns {string} - The formatted date string.
	 */
	format(date, formatStr) {
		if (!isDate(date)) {
			/* eslint-disable no-console */
			return console.error('Date is not valid')
			/* eslint-enable no-console */
		}
		return format(date, formatStr, this.formatOptions)
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Short date format, e.g., Oct 26, 2023.
	 */
	toShortDate(date) {
		return this.format(date, 'PP')
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Long date format, e.g., October 26th, 2023.
	 */
	toLongDate(date) {
		return this.format(date, 'PPP')
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Short month format, e.g., Jan, Feb, ..., Dec.
	 */
	toShortMonth(date) {
		return this.format(date, 'LLL')
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Long month format, e.g., January, February, ..., December.
	 */
	toLongMonth(date) {
		return this.format(date, 'LLLL')
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Short date with time format, e.g., Oct 26, 2023 12:00 AM.
	 */
	toShortDateTime(date) {
		return this.format(date, 'PP, p')
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Short month and day format, e.g., Oct 26.
	 */
	toShortMonthDay(date) {
		return this.format(date, this._getCustomFormat('MMM d'))
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Full month and day format, e.g., October 26.
	 */
	toFullMonthDay(date) {
		return this.format(date, this._getCustomFormat('MMMM d'))
	}

	/**
	 * @param {Date} date
	 * @returns {string} - Short month with year format, e.g., Oct, 2023.
	 */
	toShortMonthWithYear(date) {
		return this.format(date, this._getCustomFormat('LLL, yyyy'))
	}

	_overrideLocale(locale) {
		if (locale === 'es-419') {
			locale = 'es'
		}
		return locale
	}

	_getCustomFormat(key) {
		return this.customFormatsMap?.[key]?.[this.locale] ?? key
	}
}

export const dateProvider = new DateProvider({
	locale: window.language,
})
